import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ClipboardList, CalendarSearch, FileWarning, BellRing, Tags,
  ArrowRight, TrendingUp, AlertTriangle, CheckCircle2, Briefcase, Package,
  History as HistoryIcon, ClipboardCheck, FileSearch, Sparkles, X, Loader2,
} from "lucide-react";
import { api, formatZAR, MONTHS } from "../api";
import { InvoiceStatusBadge, RenewalBadge } from "../components/StatusBadge";
import BusinessFilter from "../components/BusinessFilter";
import { useBusiness } from "../context/BusinessContext";
import { toast } from "sonner";

const actions = [
  { to: "/workspace", label: "Open Client Workspace", desc: "Per-client monthly billing assistant", icon: Briefcase, testid: "action-workspace" },
  { to: "/enter-work", label: "Enter Work", desc: "Guided 8-step task entry", icon: ClipboardList, testid: "action-enter-work" },
  { to: "/outstanding", label: "Outstanding Invoices", desc: "Tasks without invoice number yet", icon: FileWarning, testid: "action-outstanding" },
  { to: "/renewals", label: "Renewal Reminders", desc: "Annual tasks due soon", icon: BellRing, testid: "action-renewals" },
  { to: "/bundles", label: "Task Bundles", desc: "Reusable groups of tasks", icon: Package, testid: "action-bundles" },
];

export default function Dashboard() {
  const now = new Date();
  const { business } = useBusiness();
  const [summary, setSummary] = useState(null);
  const [yearTotal, setYearTotal] = useState(null);
  const [renewals, setRenewals] = useState([]);
  const [outstandingTasks, setOutstandingTasks] = useState([]);
  const [missingRecurring, setMissingRecurring] = useState(null);
  const [clientsMap, setClientsMap] = useState({});
  const [statusBuckets, setStatusBuckets] = useState(null);
  // Prepare modal state
  const [prepareTarget, setPrepareTarget] = useState(null);

  const loadAll = async () => {
    const bizParam = business ? { business } : {};
    const [s, y, r, o, m, cs, cstat] = await Promise.all([
      api.get("/summary", { params: { year: now.getFullYear(), month: now.getMonth() + 1, ...bizParam } }),
      api.get("/summary", { params: { year: now.getFullYear(), ...bizParam } }),
      api.get("/renewals", { params: { window_days: 30, ...bizParam } }),
      api.get("/tasks", { params: { invoice_status: "outstanding", ...bizParam } }),
      api.get("/summary/missing-recurring", { params: { year: now.getFullYear(), month: now.getMonth() + 1, ...bizParam } }),
      api.get("/clients"),
      api.get("/summary/client-status", { params: { year: now.getFullYear(), month: now.getMonth() + 1, ...bizParam } }),
    ]);
    setSummary(s.data); setYearTotal(y.data); setRenewals(r.data);
    setOutstandingTasks(o.data.slice(0, 6));
    setMissingRecurring(m.data);
    setClientsMap(Object.fromEntries(cs.data.map(c => [c.id, c.name])));
    setStatusBuckets(cstat.data);
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line
  }, [business]);

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="np-label mb-2">Welcome back</div>
          <h1 className="text-3xl md:text-4xl font-extrabold text-[#011617]" style={{ fontFamily: "Manrope" }}>
            What do you want to do today?
          </h1>
          <p className="text-[#595959] mt-2">
            {MONTHS[now.getMonth()]} {now.getFullYear()}{business ? ` - ${business} only` : ""} - Quick actions and key metrics below.
          </p>
        </div>
        <div className="np-card !p-3">
          <BusinessFilter />
        </div>
      </div>

      {/* Guided action selector */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-10">
        {actions.map((a) => (
          <Link key={a.to} to={a.to} data-testid={a.testid} className="np-card-hover group">
            <div className="flex flex-col gap-3">
              <div className="w-11 h-11 rounded-lg bg-[#E5FFFF] flex items-center justify-center group-hover:bg-[#00ACB3] transition-colors">
                <a.icon className="w-5 h-5 text-[#0F4F51] group-hover:text-white" strokeWidth={2.25} />
              </div>
              <div>
                <h3 className="font-bold text-[#011617] text-base" style={{ fontFamily: "Manrope" }}>{a.label}</h3>
                <p className="text-xs text-[#595959] mt-1">{a.desc}</p>
              </div>
              <ArrowRight className="w-4 h-4 text-[#00ACB3] opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
          </Link>
        ))}
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-10">
        <StatCard
          label="Invoiced this month"
          value={summary ? formatZAR(summary.total_invoiced) : "—"}
          sub={`${summary ? Math.max(0, (summary.total_tasks || 0) - (summary.outstanding_count || 0)) : 0} invoiced`}
          icon={CheckCircle2}
          tone="success"
          testid="stat-invoiced-month"
        />
        <StatCard
          label="Outstanding this month"
          value={summary ? formatZAR(summary.total_outstanding) : "—"}
          sub={`${summary?.outstanding_count || 0} items`}
          icon={AlertTriangle}
          tone="warning"
          testid="stat-outstanding-month"
        />
        <StatCard
          label="Missing from last month"
          value={missingRecurring ? missingRecurring.missing_count : "—"}
          sub={missingRecurring ? `${missingRecurring.affected_clients} client${missingRecurring.affected_clients === 1 ? "" : "s"} affected` : ""}
          icon={HistoryIcon}
          tone="warning"
          testid="stat-missing-recurring"
        />
        <StatCard
          label={`Invoiced ${now.getFullYear()}`}
          value={yearTotal ? formatZAR(yearTotal.total_invoiced) : "—"}
          sub="Year to date"
          icon={TrendingUp}
          tone="info"
          testid="stat-invoiced-year"
        />
        <StatCard
          label="Renewal reminders"
          value={renewals.length}
          sub="Due in next 30 days"
          icon={BellRing}
          tone="brand"
          testid="stat-renewals"
        />
      </div>

      {/* Auto-prepared vs Needs review */}
      {statusBuckets && (statusBuckets.auto_prepared_count + statusBuckets.needs_review_count) > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Needs review */}
          <div className="np-card" data-testid="needs-review-card">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-xl font-bold text-[#011617] inline-flex items-center gap-2" style={{ fontFamily: "Manrope" }}>
                <FileSearch className="w-5 h-5 text-[#92400E]" />
                Needs review
                <span className="bg-[#FEF3C7] text-[#92400E] rounded-full px-2.5 py-0.5 text-xs font-bold">
                  {statusBuckets.needs_review_count}
                </span>
              </h2>
              <span className="text-xs text-[#595959]">{MONTHS[now.getMonth()]} {now.getFullYear()}</span>
            </div>
            {statusBuckets.needs_review.length === 0 ? (
              <p className="text-sm text-[#595959]">Nothing to review — every client is auto-prepared.</p>
            ) : (
              <ul className="divide-y divide-[#CCCCCC] max-h-80 overflow-y-auto">
                {statusBuckets.needs_review.map((c) => (
                  <li key={c.client_id} className="py-2.5" data-testid={`review-row-${c.client_id}`}>
                    <div className="flex items-center justify-between gap-3 hover:bg-[#E5FFFF]/40 -mx-2 px-2 rounded transition">
                      <Link
                        to={`/workspace?client_id=${c.client_id}&year=${now.getFullYear()}&month=${now.getMonth() + 1}`}
                        className="flex-1 min-w-0"
                      >
                        <div className="text-sm font-semibold text-[#011617] truncate">{c.client_name}</div>
                        <div className="text-xs text-[#92400E] truncate">{(c.reasons || []).join(" · ")}</div>
                      </Link>
                      <div className="text-right shrink-0">
                        <div className="text-sm font-bold text-[#011617]">{formatZAR(c.total)}</div>
                        <div className="text-[10px] uppercase tracking-wider text-[#595959]">{c.task_count} task{c.task_count === 1 ? "" : "s"}</div>
                      </div>
                      <button
                        data-testid={`prepare-btn-${c.client_id}`}
                        onClick={() => setPrepareTarget(c)}
                        className="shrink-0 bg-[#00ACB3] hover:bg-[#0F4F51] text-white text-xs font-bold rounded-lg px-3 py-2 inline-flex items-center gap-1.5"
                        title="One-click prepare: auto-create missing recurring tasks and optionally enter the invoice number"
                      >
                        <Sparkles className="w-3.5 h-3.5" /> Prepare
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Auto-prepared */}
          <div className="np-card" data-testid="auto-prepared-card">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-xl font-bold text-[#011617] inline-flex items-center gap-2" style={{ fontFamily: "Manrope" }}>
                <ClipboardCheck className="w-5 h-5 text-[#065F46]" />
                Auto-prepared
                <span className="bg-[#D1FAE5] text-[#065F46] rounded-full px-2.5 py-0.5 text-xs font-bold">
                  {statusBuckets.auto_prepared_count}
                </span>
              </h2>
              <span className="text-xs text-[#595959]">Ready to go</span>
            </div>
            {statusBuckets.auto_prepared.length === 0 ? (
              <p className="text-sm text-[#595959]">No clients fully auto-prepared yet for {MONTHS[now.getMonth()]}.</p>
            ) : (
              <ul className="divide-y divide-[#CCCCCC] max-h-80 overflow-y-auto">
                {statusBuckets.auto_prepared.map((c) => (
                  <li key={c.client_id} className="py-2.5" data-testid={`auto-row-${c.client_id}`}>
                    <Link
                      to={`/workspace?client_id=${c.client_id}&year=${now.getFullYear()}&month=${now.getMonth() + 1}`}
                      className="flex items-center justify-between gap-3 hover:bg-[#E5FFFF]/40 -mx-2 px-2 rounded transition"
                    >
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-[#011617] truncate">{c.client_name}</div>
                        <div className="text-xs text-[#065F46] truncate">
                          {c.invoice_number ? `Invoice #${c.invoice_number} · ${c.invoice_status}` : c.invoice_status}
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-sm font-bold text-[#011617]">{formatZAR(c.total)}</div>
                        <div className="text-[10px] uppercase tracking-wider text-[#595959]">{c.task_count} task{c.task_count === 1 ? "" : "s"}</div>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {/* Two panels */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="np-card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-[#011617]" style={{ fontFamily: "Manrope" }}>
              Outstanding invoices
            </h2>
            <Link to="/outstanding" data-testid="link-view-all-outstanding" className="text-sm font-semibold text-[#00ACB3] hover:text-[#0F4F51] inline-flex items-center gap-1">
              View all <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          {outstandingTasks.length === 0 ? (
            <p className="text-[#595959] text-sm">Nothing outstanding. Great work!</p>
          ) : (
            <ul className="divide-y divide-[#CCCCCC]">
              {outstandingTasks.map((t) => (
                <li key={t.id} className="py-3 flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-[#011617] truncate">
                      {clientsMap[t.client_id] || "—"}
                    </div>
                    <div className="text-xs text-[#595959] truncate">{t.notes || "Task"}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-sm font-bold text-[#011617]">{formatZAR(t.price)}</div>
                    <InvoiceStatusBadge invoiceRef={t.invoice_ref} />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="np-card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-[#011617]" style={{ fontFamily: "Manrope" }}>
              Upcoming renewals
            </h2>
            <Link to="/renewals" data-testid="link-view-all-renewals" className="text-sm font-semibold text-[#00ACB3] hover:text-[#0F4F51] inline-flex items-center gap-1">
              View all <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          {renewals.length === 0 ? (
            <p className="text-[#595959] text-sm">No renewals in the next 30 days.</p>
          ) : (
            <ul className="divide-y divide-[#CCCCCC]">
              {renewals.slice(0, 6).map((r) => (
                <li key={r.id} className="py-3 flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-[#011617] truncate">
                      {clientsMap[r.client_id] || "—"}
                    </div>
                    <div className="text-xs text-[#595959] truncate">{r.notes || "Annual task"}</div>
                  </div>
                  <RenewalBadge daysUntil={r.days_until} />
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Prepare Month modal */}
      {prepareTarget && (
        <PrepareMonthDialog
          client={prepareTarget}
          year={now.getFullYear()}
          month={now.getMonth() + 1}
          onClose={() => setPrepareTarget(null)}
          onDone={() => { setPrepareTarget(null); loadAll(); }}
        />
      )}
    </div>
  );
}

function PrepareMonthDialog({ client, year, month, onClose, onDone }) {
  const [createMissing, setCreateMissing] = useState(client.missing_count > 0);
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [invoiceStatusChoice, setInvoiceStatusChoice] = useState("numbered");
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);

  const showInvoiceFields = client.invoice_status === "draft";

  const apply = async () => {
    setBusy(true);
    let createdCount = 0;
    try {
      if (createMissing && client.missing_count > 0) {
        const r = await api.post(`/clients/${client.client_id}/ensure-recurring`, { year, month });
        createdCount = r.data?.created_count || 0;
      }
      let invoiceMsg = "";
      if (showInvoiceFields && invoiceNumber.trim()) {
        // Fetch invoice id for this period
        const inv = await api.get(`/invoices/by-period`, { params: { client_id: client.client_id, year, month } });
        const invId = inv.data?.id;
        if (invId) {
          await api.post(`/invoices/${invId}/assign-number`, {
            invoice_number: invoiceNumber.trim(),
            status: invoiceStatusChoice,
          });
          const label = invoiceStatusChoice === "on_hold" ? "On Hold" : "Numbered";
          invoiceMsg = ` · Invoice ${invoiceNumber.trim()} saved (${label})`;
        }
      }
      toast.success(`${client.client_name}: ${createdCount} task${createdCount === 1 ? "" : "s"} added${invoiceMsg}`);
      setCreated({ createdCount, invoiceNumber: invoiceNumber.trim() });
      onDone();
    } catch (e) {
      toast.error("Could not prepare month");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      data-testid="prepare-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="text-lg font-extrabold text-[#011617]" style={{ fontFamily: "Manrope" }}>
              Prepare {client.client_name}
            </h3>
            <p className="text-xs text-[#595959] mt-1">{MONTHS[month - 1]} {year}</p>
          </div>
          <button onClick={onClose} className="text-[#595959] hover:text-[#011617]" data-testid="prepare-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Reasons */}
        {(client.reasons || []).length > 0 && (
          <div className="bg-[#FEF3C7] border border-[#FBBF24] rounded-lg p-3 mb-4">
            <div className="text-xs uppercase tracking-wider text-[#92400E] font-bold mb-1">What's missing</div>
            <ul className="text-sm text-[#92400E]">
              {client.reasons.map((r, i) => <li key={i}>• {r}</li>)}
            </ul>
          </div>
        )}

        {/* Step 1: missing recurring tasks */}
        {client.missing_count > 0 && (
          <label className="flex items-start gap-2 p-3 rounded-lg border border-[#CCCCCC] mb-3 cursor-pointer hover:border-[#00ACB3]">
            <input
              type="checkbox"
              data-testid="prepare-create-missing"
              checked={createMissing}
              onChange={e => setCreateMissing(e.target.checked)}
              className="mt-0.5"
            />
            <div>
              <div className="font-bold text-sm text-[#011617]">
                Auto-create {client.missing_count} recurring task{client.missing_count === 1 ? "" : "s"}
              </div>
              <div className="text-xs text-[#595959]">
                Adds every ACTIVE recurring template that's due this month. Paused/Stopped templates are skipped.
              </div>
            </div>
          </label>
        )}

        {/* Step 2: invoice number */}
        {showInvoiceFields && (
          <div className="border border-[#CCCCCC] rounded-lg p-3 mb-4">
            <div className="text-xs uppercase tracking-wider text-[#0F4F51] font-bold mb-2">
              Invoice number (optional)
            </div>
            <input
              data-testid="prepare-invoice-number"
              value={invoiceNumber}
              onChange={e => setInvoiceNumber(e.target.value)}
              placeholder="Invoice # from your accounting system"
              className="w-full px-3 py-2 rounded-lg border border-[#CCCCCC] outline-none focus:border-[#00ACB3] mb-2"
            />
            <select
              data-testid="prepare-status-choice"
              value={invoiceStatusChoice}
              onChange={e => setInvoiceStatusChoice(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-[#CCCCCC] outline-none focus:border-[#00ACB3] font-semibold text-sm"
            >
              <option value="numbered">Numbered (track normally)</option>
              <option value="on_hold">On Hold (keep outstanding)</option>
            </select>
            <p className="text-[11px] text-[#595959] mt-2">Leave blank to keep the invoice in Draft and enter the number later.</p>
          </div>
        )}

        <div className="flex items-center justify-end gap-2 pt-2">
          <button
            onClick={onClose}
            disabled={busy}
            className="text-sm text-[#595959] hover:text-[#011617] underline"
            data-testid="prepare-cancel"
          >
            Cancel
          </button>
          <button
            data-testid="prepare-apply"
            onClick={apply}
            disabled={busy || (!createMissing && !invoiceNumber.trim())}
            className="bg-[#00ACB3] hover:bg-[#0F4F51] text-white font-semibold rounded-lg px-4 py-2 inline-flex items-center gap-2 disabled:opacity-50"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {busy ? "Preparing…" : "Apply"}
          </button>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, sub, icon: Icon, tone, testid }) {
  const toneClasses = {
    success: "bg-[#D1FAE5] text-[#065F46]",
    warning: "bg-[#FEF3C7] text-[#92400E]",
    info: "bg-[#E5FFFF] text-[#0F4F51]",
    brand: "np-brand-stripe text-white",
  };
  return (
    <div data-testid={testid} className="np-card flex items-start justify-between gap-4">
      <div>
        <div className="np-label">{label}</div>
        <div className="text-2xl font-extrabold text-[#011617] mt-2" style={{ fontFamily: "Manrope" }}>
          {value}
        </div>
        <div className="text-xs text-[#595959] mt-1">{sub}</div>
      </div>
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${toneClasses[tone]}`}>
        <Icon className="w-5 h-5" strokeWidth={2.25} />
      </div>
    </div>
  );
}
